import { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useAdminRole } from "@/hooks/useAdminRole";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2, CheckCircle, AlertCircle, Play, RotateCcw, Zap } from "lucide-react";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/errors";
import type { AnyValue } from "@/lib/rowTypes";

interface DBLesson {
  lesson_id: string;
  lesson_name: string;
  course_name: string;
}

interface ParsedLesson {
  lessonName: string;
  rawText: string;
}

interface ParsedCourse {
  courseName: string;
  dbCourseName: string;
  lessons: ParsedLesson[];
}

interface ProcessedQuestion {
  question: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  correct_answer: string;
  explanation: string;
}

type CourseStatus = "idle" | "processing" | "done" | "error";

// Map uploaded file course names to DB course names
const COURSE_NAME_MAP: Record<string, string> = {
  "Financial Foundations": "Financial Foundations",
  "Banking Basics": "Banking & Cash Management",
  "Budgeting & Spending": "Saving & Budgeting",
  "Credit & Debt Management": "Credit & Debt Management",
  "Financial Psychology": "Behavioral Finance",
  "Career & Income Strategy": "Career & Income Strategy",
  "Transportation & Vehicle Ownership": "Transportation & Vehicle Ownership",
  "Insurance & Risk Management": "Insurance & Risk Protection",
  "Family & Household Finance": "Family Finance",
  "Investing Fundamentals": "Investing Fundamentals",
  "Market Knowledge": "Investing Fundamentals",
  "Retirement & Investment Accounts": "Retirement & Investment Accounts",
  "Taxes": "Taxes",
  "Advanced Financial Strategy": "Advanced Financial Strategy",
  "Homeownership": "Real Estate (Homeownership)",
  "Real Estate Investing": "Real Estate Investing",
  "Real Estate Development": "Real Estate Development",
  "Entrepreneurship Fundamentals": "Business Fundamentals",
  "Startup Strategy": "Startups & Entrepreneurship",
  "Business Growth & Scaling": "Business & Startup Expansion",
  "Business Finance & Metrics": "Business & Startup Expansion",
  "Macroeconomics": "Macroeconomics & Systems",
  "Financial Scams & Fraud Awareness": "Financial Scams & Fraud Awareness",
  "Company Case Studies": "Company Case Studies",
};

function parseMCQBank(text: string): ParsedCourse[] {
  const courses: ParsedCourse[] = [];
  const lines = text.split("\n");
  
  let currentCourse: ParsedCourse | null = null;
  let currentLesson: ParsedLesson | null = null;
  let lessonLines: string[] = [];

  for (const line of lines) {
    // Course header (# Course Name)
    if (line.startsWith("# ") && !line.startsWith("##")) {
      // Save previous lesson
      if (currentLesson && currentCourse) {
        currentLesson.rawText = lessonLines.join("\n");
        currentCourse.lessons.push(currentLesson);
        currentLesson = null;
        lessonLines = [];
      }
      // Save previous course
      if (currentCourse && currentCourse.lessons.length > 0) {
        courses.push(currentCourse);
      }
      
      const courseName = line.replace("# ", "").trim();
      const dbName = COURSE_NAME_MAP[courseName] || courseName;
      currentCourse = { courseName, dbCourseName: dbName, lessons: [] };
      continue;
    }

    // Lesson header (## Lesson Name)
    if (line.startsWith("## ")) {
      // Save previous lesson
      if (currentLesson && currentCourse) {
        currentLesson.rawText = lessonLines.join("\n");
        currentCourse.lessons.push(currentLesson);
        lessonLines = [];
      }
      
      const lessonName = line.replace("## ", "").trim();
      currentLesson = { lessonName, rawText: "" };
      continue;
    }

    if (currentLesson) {
      lessonLines.push(line);
    }
  }

  // Save last lesson and course
  if (currentLesson && currentCourse) {
    currentLesson.rawText = lessonLines.join("\n");
    currentCourse.lessons.push(currentLesson);
  }
  if (currentCourse && currentCourse.lessons.length > 0) {
    courses.push(currentCourse);
  }

  return courses;
}

const MCQProcessor = () => {
  const { user } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdminRole();
  const navigate = useNavigate();
  const [dbLessons, setDbLessons] = useState<DBLesson[]>([]);
  const [parsedCourses, setParsedCourses] = useState<ParsedCourse[]>([]);
  const [courseStatuses, setCourseStatuses] = useState<Record<string, CourseStatus>>({});
  const [courseErrors, setCourseErrors] = useState<Record<string, string>>({});
  const [courseResults, setCourseResults] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const abortRef = useRef(false);

  useEffect(() => {
    const init = async () => {
      // Load DB lessons
      const { data: lessons } = await supabase
        .from("lessons")
        .select("lesson_id, lesson_name, course_name")
        .order("course_name")
        .order("lesson_order");
      setDbLessons(lessons || []);

      // Load and parse MCQ bank
      try {
        const res = await fetch("/mcq-bank.md");
        const text = await res.text();
        const courses = parseMCQBank(text);
        setParsedCourses(courses);
        
        // Initialize statuses
        const statuses: Record<string, CourseStatus> = {};
        courses.forEach(c => { statuses[c.courseName] = "idle"; });
        setCourseStatuses(statuses);
      } catch (err) {
        toast.error("Failed to load MCQ bank file");
      }
      
      setLoading(false);
    };
    init();
  }, []);

  const findLessonId = useCallback((lessonName: string, dbCourseName: string): string | null => {
    // Try exact match first
    const exact = dbLessons.find(
      l => l.lesson_name.toLowerCase() === lessonName.toLowerCase() && 
           l.course_name === dbCourseName
    );
    if (exact) return exact.lesson_id;
    
    // Try fuzzy match within the course
    const fuzzy = dbLessons.find(
      l => l.course_name === dbCourseName && 
           (l.lesson_name.toLowerCase().includes(lessonName.toLowerCase()) ||
            lessonName.toLowerCase().includes(l.lesson_name.toLowerCase()))
    );
    if (fuzzy) return fuzzy.lesson_id;

    // Try across all courses
    const global = dbLessons.find(
      l => l.lesson_name.toLowerCase() === lessonName.toLowerCase()
    );
    if (global) return global.lesson_id;

    return null;
  }, [dbLessons]);

  const parseInvokeError = useCallback(async (error: AnyValue): Promise<{ message: string; status?: number; code?: string }> => {
    const fallback = error?.message || "Edge function failed";
    const context = error?.context;

    if (!context || typeof context !== "object" || typeof context.status !== "number") {
      return { message: fallback };
    }

    const status = context.status as number;
    try {
      const payload = await context.json();
      return {
        message: payload?.error || fallback,
        status,
        code: typeof payload?.code === "string" ? payload.code : undefined,
      };
    } catch {
      return { message: fallback, status };
    }
  }, []);

  const processCourse = useCallback(async (course: ParsedCourse) => {
    setCourseStatuses(prev => ({ ...prev, [course.courseName]: "processing" }));
    
    try {
      // Build the course section text for AI
      const courseSection = `# ${course.courseName}\n\n` + 
        course.lessons.map(l => `## ${l.lessonName}\n${l.rawText}`).join("\n\n");

      const { data, error } = await supabase.functions.invoke("rewrite-questions", {
        body: { courseSection },
      });

      if (error) {
        const parsed = await parseInvokeError(error);
        const enriched = new Error(parsed.message) as Error & { status?: number; code?: string };
        enriched.status = parsed.status;
        enriched.code = parsed.code;
        throw enriched;
      }

      if (data?.error) {
        const enriched = new Error(data.error) as Error & { status?: number; code?: string };
        enriched.status = typeof data?.status === "number" ? data.status : undefined;
        enriched.code = typeof data?.code === "string" ? data.code : undefined;
        throw enriched;
      }

      const rewrittenLessons = data.lessons;
      if (!Array.isArray(rewrittenLessons)) {
        throw new Error("AI returned invalid format");
      }

      // Upsert questions into DB
      let insertedCount = 0;
      for (const lesson of rewrittenLessons) {
        const lessonName = lesson.lesson_name;
        const lessonId = findLessonId(lessonName, course.dbCourseName);
        
        if (!lessonId) {
          console.warn(`No DB lesson found for: "${lessonName}" in ${course.dbCourseName}`);
          continue;
        }

        const questions: ProcessedQuestion[] = lesson.questions || [];
        if (questions.length === 0) continue;

        // Delete existing questions for this lesson
        await supabase.from("questions").delete().eq("lesson_id", lessonId);

        // Insert new questions
        const inserts = questions.map((q: ProcessedQuestion) => ({
          lesson_id: lessonId,
          question: q.question,
          option_a: q.option_a,
          option_b: q.option_b,
          option_c: q.option_c,
          option_d: q.option_d,
          correct_answer: q.correct_answer,
          explanation: q.explanation || null,
        }));

        const { error: insertError } = await supabase.from("questions").insert(inserts);
        if (insertError) {
          console.error(`Insert error for ${lessonName}:`, insertError);
        } else {
          insertedCount += inserts.length;
        }
      }

      setCourseResults(prev => ({ ...prev, [course.courseName]: insertedCount }));
      setCourseStatuses(prev => ({ ...prev, [course.courseName]: "done" }));
    } catch (err: unknown) {
      console.error(`Error processing ${course.courseName}:`, err);
      const message = getErrorMessage(err) || "Edge function failed";
      const status = (err as AnyValue)?.status;
      const code = (err as AnyValue)?.code;

      setCourseErrors(prev => ({ ...prev, [course.courseName]: message }));
      setCourseStatuses(prev => ({ ...prev, [course.courseName]: "error" }));

      if (status === 402 || code === "PAYMENT_REQUIRED") {
        abortRef.current = true;
        toast.error("Not enough Lovable AI credits. Add credits in Settings → Workspace → Usage, then retry.");
      } else if (status === 429 || code === "RATE_LIMITED") {
        abortRef.current = true;
        toast.error("AI rate limit hit. Please wait a moment, then retry.");
      }
    }
  }, [findLessonId, parseInvokeError]);

  const processAll = useCallback(async () => {
    setProcessing(true);
    abortRef.current = false;

    for (const course of parsedCourses) {
      if (abortRef.current) break;
      if (courseStatuses[course.courseName] === "done") continue;
      await processCourse(course);
      // Small delay between courses to avoid rate limiting
      await new Promise(r => setTimeout(r, 2000));
    }

    setProcessing(false);
    if (abortRef.current) {
      toast.info("MCQ processing stopped.");
      return;
    }
    toast.success("MCQ processing complete!");
  }, [parsedCourses, courseStatuses, processCourse]);

  const stopProcessing = () => {
    abortRef.current = true;
  };

  const resetCourse = (courseName: string) => {
    setCourseStatuses(prev => ({ ...prev, [courseName]: "idle" }));
    setCourseErrors(prev => { const n = { ...prev }; delete n[courseName]; return n; });
    setCourseResults(prev => { const n = { ...prev }; delete n[courseName]; return n; });
  };

  const doneCount = Object.values(courseStatuses).filter(s => s === "done").length;
  const errorCount = Object.values(courseStatuses).filter(s => s === "error").length;
  const totalQuestions = Object.values(courseResults).reduce((a, b) => a + b, 0);

  if (loading || adminLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-lg font-semibold text-foreground">Access Denied</p>
          <p className="text-muted-foreground">You don't have permission to access this page.</p>
          <Button onClick={() => navigate("/dashboard")}>Go to Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/80 backdrop-blur-md sticky top-0 z-50">
        <div className="container max-w-4xl mx-auto flex items-center justify-between py-4 px-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/admin/upload")}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="font-heading font-bold text-lg text-foreground">
              MCQ Rewrite Processor
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">
              {doneCount}/{parsedCourses.length} courses • {totalQuestions} questions
              {errorCount > 0 && ` • ${errorCount} errors`}
            </span>
            {processing ? (
              <Button variant="destructive" size="sm" onClick={stopProcessing}>
                Stop
              </Button>
            ) : (
              <Button size="sm" onClick={processAll} className="gap-2">
                <Zap className="w-4 h-4" />
                Process All
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="container max-w-4xl mx-auto py-6 px-4 space-y-3">
        <div className="rounded-xl border border-border bg-card/50 p-4 mb-6">
          <p className="text-sm text-muted-foreground">
            This tool sends each course to AI for question rewriting, then upserts the improved questions into the database. 
            Found <strong>{parsedCourses.length}</strong> courses with <strong>{parsedCourses.reduce((a, c) => a + c.lessons.length, 0)}</strong> lessons in the MCQ bank.
          </p>
        </div>

        {parsedCourses.map(course => {
          const status = courseStatuses[course.courseName] || "idle";
          const error = courseErrors[course.courseName];
          const result = courseResults[course.courseName];

          return (
            <div
              key={course.courseName}
              className={`rounded-xl border p-4 flex items-center justify-between gap-4 ${
                status === "done" ? "border-emerald-500/30 bg-emerald-500/5" :
                status === "error" ? "border-red-500/30 bg-red-500/5" :
                status === "processing" ? "border-primary/30 bg-primary/5" :
                "border-border bg-card"
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  {status === "processing" && <Loader2 className="w-4 h-4 animate-spin text-primary flex-shrink-0" />}
                  {status === "done" && <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />}
                  {status === "error" && <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />}
                  <h3 className="font-heading font-semibold text-sm text-foreground truncate">
                    {course.courseName}
                  </h3>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {course.lessons.length} lessons → DB: {course.dbCourseName}
                  {result !== undefined && ` • ${result} questions inserted`}
                </p>
                {error && (
                  <p className="text-xs text-red-400 mt-1 truncate">{error}</p>
                )}
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                {(status === "idle" || status === "error") && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => processCourse(course)}
                    disabled={processing}
                    className="gap-1"
                  >
                    {status === "error" ? <RotateCcw className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                    {status === "error" ? "Retry" : "Process"}
                  </Button>
                )}
                {status === "done" && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => resetCourse(course.courseName)}
                    className="gap-1 text-xs"
                  >
                    <RotateCcw className="w-3 h-3" /> Reset
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </main>
    </div>
  );
};

export default MCQProcessor;
